import { Button, Checkbox, Divider, FormModifier, IconLogoMark, Link } from '@hudl/uniform-ui-components';
import React, { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { LoginResponse } from 'requests/rest/login';
import ErrorDisplay from '../../shared/ErrorDisplay';
import LoginInput from '../LoginInput';
import * as styles from './styles.scss';
import g11n from 'hudl-g11n';

interface LoginFormProps {
  email: string;
  forward?: string | null;
  initialError?: string;
  hudlLogoClassName?: string;
  formClassName?: string;
  setEmail: (email: string) => void;
  onLogin: (email: string, password: string, rememberMe: boolean) => Promise<LoginResponse>;
  onHelpClick: () => void;
}

interface Error {
  message: ReactNode;
  // Sticky errors will not be cleared on typing and will not disable submitting
  isSticky: boolean;
}

const parseInitialError = (initialError: string | undefined): Error | null => {
  if (!initialError) {
    return null;
  }

  return {
    message: initialError,
    isSticky: true,
  };
};
const parseLoginError = (loginAttemptCount: number, onHelpClick: () => void): Error => ({
  message: (
    <Fragment>
      {/* Show a "Can't remember" text if they fail too many times */}
      {loginAttemptCount > 5 ? g11n.format('login_bad_credential') : g11n.format('login_bad_credentials_many')}

      <Link className="uni-margin--quarter--left" onClick={onHelpClick}>
        {g11n.format('login_need_help')}
      </Link>
    </Fragment>
  ),
  isSticky: false,
});

const LoginForm = (props: LoginFormProps) => {
  const { email, forward, initialError, hudlLogoClassName, formClassName, setEmail, onLogin, onHelpClick } = props;

  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef?.current?.focus();
  }, [emailRef]);

  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<Error | null>(parseInitialError(initialError));
  const [loginAttemptCount, setLoginAttemptCount] = useState(0);

  const [isLoading, setIsLoading] = useState(false);

  const ssoUrl = new URL('/app/auth/login/organization', window.location.origin);
  if (forward) {
    ssoUrl.searchParams.append('forward', forward);
  }

  const onLoginClick = useCallback(
    async e => {
      e?.preventDefault();
      setLoginAttemptCount(loginAttemptCount + 1);
      setIsLoading(true);

      const result = await onLogin(email, password, rememberMe);

      setIsLoading(false);

      if (result && result?.success) {
        window.location.replace(result.forward ?? '/home');
      } else {
        setError(parseLoginError(loginAttemptCount, onHelpClick));
      }
    },
    [email, loginAttemptCount, onHelpClick, onLogin, password, rememberMe],
  );

  const clearError = useCallback(() => {
    // Only clear if the error is not sticky
    if (error !== null && !error.isSticky) {
      setError(null);
    }
  }, [error]);
  const onSetPassword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
      clearError();
    },
    [clearError],
  );
  const onSetEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      clearError();
    },
    [clearError, setEmail],
  );

  return (
    <form className={styles.formContainer}>
      <Link href="/" type="wrapper" className={`${styles.hudlLogoContainer} ${hudlLogoClassName ?? ''}`}>
        <IconLogoMark qaId="hudl-logo" size="large" />
      </Link>

      <FormModifier
        className={`${styles.formModifierContainer} ${formClassName ?? ''}`}
        formSize="large"
        density="default">
        <LoginInput
          id="email"
          type="email"
          name="email"
          label={g11n.format('login_email')}
          innerRef={emailRef}
          className="uni-margin--one--top"
          onChange={onSetEmail}
          value={email}
          qaId="email-input"
          autoComplete="email"
          required
        />

        <LoginInput
          id="password"
          type="password"
          name="password"
          label={g11n.format('login_password')}
          className="uni-margin--one--top uni-margin--one--bottom"
          onChange={onSetPassword}
          value={password}
          qaId="password-input"
          autoComplete="current-password"
          required
        />

        <ErrorDisplay qaId="error-display">{error?.message}</ErrorDisplay>

        <Button
          id="logIn"
          type="submit"
          size="large"
          isDisabled={isLoading || (error !== null && !error.isSticky)}
          onClick={onLoginClick}
          className="uni-margin--one--top"
          qaId="login-btn">
          {g11n.format('login_login')}
        </Button>

        <div className={styles.footerContainer}>
          <Checkbox
            label={g11n.format('login_remember_me')}
            onChange={useCallback(() => setRememberMe(!rememberMe), [rememberMe])}
            isChecked={rememberMe}
            qaId="remember-me-checkbox"
          />

          <Link onClick={onHelpClick} href="#" qaId="need-help-link">
            {g11n.format('login_need_help')}
          </Link>
        </div>

        <Divider className="uni-margin--one-and-half--top uni-margin--one-and-half--bottom" />

        <Link href={ssoUrl.toString()} className="uni-margin--three--bottom" type="wrapper">
          <Button
            buttonType="subtle"
            buttonStyle="minimal"
            size="large"
            className={styles.logInWithAnOrganizationButton}
            qaId="log-in-with-organization-btn">
            {g11n.format('login_login_with_organization')}
          </Button>
        </Link>
      </FormModifier>
    </form>
  );
};

export default LoginForm;
